/**
 * Action helpers for the Stripe checkout / billing-portal entry
 * points. Previously lived inside the `usePayment()` aggregator hook
 * alongside subscription-status reads, which conflated server data
 * with user-triggered actions. Now they're a thin standalone hook
 * the upgrade surfaces consume directly.
 *
 * - `handleCheckout(plan, cycle, extra?)` POSTs to
 *   `/api/billing/create-checkout-session` and redirects on success.
 *   Takes the selection as explicit params (read by the caller from
 *   `usePaymentSelectionStore`) so the hook itself doesn't depend on
 *   any store.
 *
 * - `handleManageBilling(orgSlug?)` POSTs to
 *   `/api/billing/create-portal-session` (scoped to the org when
 *   given) and redirects.
 *
 * Both swallow errors into a toast — the call sites are buttons in
 * upgrade modals where there's nothing meaningful to do on failure
 * other than tell the user it didn't work.
 */

"use client";

import { toast } from "sonner";

import {
  createCheckoutSession,
  createPortalSession,
} from "@/services/billingApi";
import type { BillingCycle, PlanType } from "@/stores/paymentSelection/types";

export interface CheckoutExtras {
  context?: string;
  project_id?: string;
  org_slug?: string;
}

export function useCheckout() {
  async function handleCheckout(
    plan: PlanType | null,
    billingCycle: BillingCycle | null,
    extra?: CheckoutExtras,
  ): Promise<void> {
    try {
      const url = await createCheckoutSession({
        plan,
        billingCycle,
        ...extra,
      });
      if (url) {
        window.location.href = url;
      } else {
        toast.error("Couldn't start checkout. Please try again.");
      }
    } catch (error) {
      console.error("Failed to create checkout session:", error);
      const message =
        error instanceof Error
          ? error.message
          : "Couldn't start checkout. Please try again.";
      toast.error(message);
    }
  }

  async function handleManageBilling(orgSlug?: string): Promise<void> {
    try {
      const url = await createPortalSession(orgSlug);
      if (url) {
        window.location.href = url;
      } else {
        toast.error("Couldn't open billing portal. Please try again.");
      }
    } catch (error) {
      console.error("Failed to create portal session:", error);
      const message =
        error instanceof Error
          ? error.message
          : "Couldn't open billing portal. Please try again.";
      toast.error(message);
    }
  }

  return { handleCheckout, handleManageBilling };
}
